// Port of ios/Sources/ViewModels/TypingTracker.swift.
//
// Who is typing, where, right now. Fed by the socket's `typing` events, read by the open
// conversation. Entries expire on their own — the server sends no "stopped typing", so a
// six-second silence IS the stop signal, the same rule every Mattermost client uses.
//
// The Swift class is an `ObservableObject` that reads `Date()` and runs its own 2-second prune
// `Timer`. Here time is a `now` parameter (src/core has no clock of its own) and pruning is a
// method: the owner (a view model) calls `prune(now)` on its own timer while `hasEntries` is true
// and re-renders when it returns true — the Swift timer's exact cadence and lifecycle.

/** How long one `typing` event keeps its indicator alive. */
export const holdMillis = 6_000;

/** How often the owner should call `prune` while anything is tracked (the Swift timer's period). */
export const pruneIntervalMillis = 2_000;

/**
 * A scope is one composer's audience: `(channelId, "")` for the channel, `(channelId, rootId)`
 * for a thread — an exact split, so the two never borrow each other's typers.
 */
export function scopeKey(channelId: string, parentId: string): string {
  return channelId + '\n' + parentId;
}

export class TypingTracker {
  /** scope key → (userId → the instant (epoch ms) their indicator expires). */
  private typingByScope = new Map<string, Map<string, number>>();

  /** Read-only view of the current state (for the owner's change detection). */
  get scopes(): ReadonlyMap<string, ReadonlyMap<string, number>> {
    return this.typingByScope;
  }

  get hasEntries(): boolean {
    return this.typingByScope.size > 0;
  }

  note(userId: string, channelId: string, parentId: string, now: number): void {
    const scope = scopeKey(channelId, parentId);
    let users = this.typingByScope.get(scope);
    if (!users) {
      users = new Map();
      this.typingByScope.set(scope, users);
    }
    users.set(userId, now + holdMillis);
  }

  /**
   * A user's message ARRIVING removes their indicator instantly — the message is the louder
   * signal, and "X is typing" under X's fresh message reads as a glitch.
   */
  clear(userId: string, channelId: string, parentId: string): void {
    const scope = scopeKey(channelId, parentId);
    const users = this.typingByScope.get(scope);
    if (!users) return;
    users.delete(userId);
    if (users.size === 0) this.typingByScope.delete(scope);
  }

  /** The user ids typing in this scope right now, sorted (stable order for the label). */
  typistIds(channelId: string, parentId: string, now: number): string[] {
    const users = this.typingByScope.get(scopeKey(channelId, parentId));
    if (!users) return [];
    return [...users]
      .filter(([, expires]) => expires > now)
      .map(([userId]) => userId)
      .sort();
  }

  /** Drops expired entries and empty scopes. Returns whether anything was removed. */
  prune(now: number): boolean {
    let changed = false;
    for (const [scope, users] of this.typingByScope) {
      for (const [userId, expires] of users) {
        if (!(expires > now)) {
          users.delete(userId);
          changed = true;
        }
      }
      if (users.size === 0) {
        this.typingByScope.delete(scope);
        changed = true;
      }
    }
    return changed;
  }
}
